import React, { useState } from 'react';
import { ExternalLink, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InfraredTherapy = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const products = [
    {
      name: 'Thera360 Plus Infrared Sauna',
      description: 'Patented full spectrum infrared portable sauna for complete wellness at home.',
      features: ['Full spectrum infrared', 'Portable design', 'Easy setup', 'Gemstone technology']
    },
    {
      name: 'TheraPro PEMF + Infrared + Red Light Pad',
      description: 'Multi-modal healing pad combining three powerful technologies.',
      features: ['PEMF therapy', 'Infrared heat', 'Red light therapy', 'Targeted healing']
    },
    {
      name: 'Thera Tri-Lite Red Light Therapy',
      description: 'Professional-grade red light therapy for skin health and recovery.',
      features: ['Multiple wavelengths', 'Large coverage area', 'Adjustable intensity', 'Timer function']
    },
    {
      name: 'Therasage Healing Pads',
      description: 'Therapeutic heating pads with infrared technology for pain relief.',
      features: ['Natural heat therapy', 'Gemstone infused', 'Flexible design', 'Safe & effective']
    }
  ];

  const studies = [
    {
      title: 'Infrared Sauna Therapy Benefits',
      summary: 'Research indicates that infrared sauna therapy may support cardiovascular health, promote detoxification, and aid in muscle recovery. Studies show improvements in circulation and potential benefits for chronic conditions when used regularly.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5941775/'
    },
    {
      title: 'Red Light Therapy and Cellular Function',
      summary: 'Clinical studies demonstrate that specific wavelengths of red and near-infrared light can stimulate cellular energy production (ATP), reduce inflammation, and promote tissue repair. Research shows applications in wound healing and skin rejuvenation.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6422597/'
    },
    {
      title: 'PEMF Therapy Research',
      summary: 'Pulsed electromagnetic field (PEMF) therapy has been studied for its potential to support bone healing, reduce pain, and improve cellular function. Research suggests PEMF may enhance circulation and cellular repair processes.',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3743972/'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      await axios.post(`${API}/inquiries`, {
        section: 'infrared-therapy',
        ...formData
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(13, 40, 71, 0.8), rgba(13, 40, 71, 0.8)), url(https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/bix546un_image.png)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <h1 className="text-5xl font-bold mb-4">Red Light & Infrared Therapy</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Experience 25+ years of trusted healing technology with Therasage's full spectrum infrared solutions.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0D2847] mb-6">About Therasage</h2>
              <p className="text-gray-600 mb-4">
                For over 25 years, Therasage has been the #1 trusted name in red light and infrared technology. 
                Their innovative products combine ancient healing wisdom with modern science to deliver therapeutic benefits.
              </p>
              <p className="text-gray-600 mb-4">
                From portable infrared saunas to healing pads and red light therapy devices, Therasage products are 
                designed with patented gemstone technology and grounding features for enhanced healing benefits.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Detoxification support</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Pain relief</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Improved circulation</span>
                </div>
                <div className="flex items-start">
                  <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Skin rejuvenation</span>
                </div>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/bix546un_image.png" 
                alt="Infrared Therapy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-8 text-center">Therasage Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((product, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <CardDescription>{product.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-600">
                        <Check className="text-green-500 mr-2 flex-shrink-0" size={16} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Studies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-4 text-center">Scientific Research</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Infrared and red light therapy are supported by extensive scientific research. Explore key studies:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {studies.map((study, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{study.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">{study.summary}</p>
                  <a 
                    href={study.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#0D2847] hover:underline text-sm font-medium flex items-center"
                  >
                    Read Full Study <ExternalLink size={14} className="ml-1" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Product Inquiry Form */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">Interested in Therasage Products?</CardTitle>
              <CardDescription className="text-center">
                Contact us to learn more about infrared therapy and find the perfect solution for your wellness needs.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input 
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input 
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea 
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-[#0D2847]" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
                {submitStatus === 'success' && (
                  <p className="text-green-600 text-center">Thank you! We'll be in touch soon.</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-600 text-center">Something went wrong. Please try again.</p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default InfraredTherapy;
